import React, { useState, useRef, useEffect } from 'react';
import Icons from '../../../../assets/index';
import { CustomDropdownInputProps, Option } from './types/CustomInputsTypes';

const CustomDropdownInput: React.FC<CustomDropdownInputProps> = ({
  label,
  name,
  value,
  options,
  onChange,
  placeholder = 'Select an option',
  required = false,
  description,
  width = 'w-full',
  isEditable = true,
  className = '',
  onFocus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the selected option label
  const selectedOption = options.find((option) => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (option: Option) => {
    onChange({
      target: {
        name,
        value: option.value,
        type: 'select',
      },
    });
    setIsOpen(false);
  };

  return (
    <div className={`flex flex-col ${width} ${className}`}>
      <label className="flex mt-4  text-ds-gray-800 font-semibold">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {description && <p className="text-ds-gray-300 text-xs mt-1">{description}</p>}

      <div className="relative py-2" ref={dropdownRef}>
        <div
          className={`border-2 w-[400px] h-[40px] rounded-md py-2 px-3 flex justify-between items-center 
            ${isEditable ? 'cursor-pointer bg-white border-ds-gray-100 hover:border-blue-1700  ' : 'bg-gray-100 border-none cursor-not-allowed'} 
            ${isOpen ? 'border-blue-1700 ' : 'bg-ds-gray-50 '}`}
          onClick={() => {
            if (isEditable) {
              setIsOpen(!isOpen);
              onFocus?.();
            }
          }}
        >
          <span className={`${!value ? 'text-ds-gray-300 text-xs' : 'text-gray-2400  text-xs'}`}>
            {displayValue || placeholder}
          </span>
          <Icons.arrowDownIcon isOpen={isOpen} />
        </div>

        {isOpen && isEditable && (
          <div className="absolute top-full w-[400px] left-0 right-0 mt-1  rounded-md bg-white shadow-lg z-10 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <div
                key={option.value}
                className={`py-2 px-3 cursor-pointer hover:bg-blue-700 text-xs font-normal
                  ${option.value === value ? 'bg-blue-50 text-blue-1700 hover:bg-blue-50' : 'text-gray-2400'}`}
                onClick={() => handleOptionSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDropdownInput;
